import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message, Popconfirm,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  EditOutlined,
  HomeOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
  RobotOutlined,
  SearchOutlined,
  UserOutlined,
  FileTextOutlined, NumberOutlined, ScheduleOutlined, PlayCircleOutlined, DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const API = '/api/v1/jj';
const h = () => ({
  Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`,
  'Content-Type': 'application/json',
});

const getPropertyStatus = (t) => ({
  available: { label: t('jjprop.statusAvailable', 'Available'), color: 'green' },
  rented: { label: t('jjprop.statusRented', 'Rented'), color: 'blue' },
  sold: { label: t('jjprop.statusSold', 'Sold'), color: 'purple' },
  not_available: { label: t('jjprop.statusNotAvailable', 'Not Available'), color: 'default' },
  needs_followup: { label: t('jjprop.statusNeedsFollowup', 'Needs Follow-up'), color: 'orange' },
  viewing_scheduled: { label: t('jjprop.statusViewing', 'Viewing Scheduled'), color: 'cyan' },
  paused: { label: t('jjprop.statusPaused', 'Paused'), color: 'volcano' },
});

const getListingTypes = (t) => ({
  rent: { label: t('jjprop.listRent', 'Rent'), color: 'green' },
  sale: { label: t('jjprop.listSale', 'Sale'), color: 'gold' },
  rent_sale: { label: t('jjprop.listRentSale', 'Rent / Sale'), color: 'geekblue' },
});

const getCallResult = (t) => ({
  available: { label: t('jjprop.callStillAvailable', 'Still Available'), color: 'green' },
  not_available: { label: t('jjprop.callNotAvailable', 'Not Available'), color: 'default' },
  rented: { label: t('jjprop.statusRented', 'Rented'), color: 'blue' },
  sold: { label: t('jjprop.statusSold', 'Sold'), color: 'purple' },
  call_back: { label: t('jjprop.callBack', 'Call Back Later'), color: 'orange' },
  viewing_scheduled: { label: t('jjprop.statusViewing', 'Viewing Scheduled'), color: 'cyan' },
  wrong_number: { label: t('jjprop.wrongNumber', 'Wrong Number'), color: 'red' },
  unknown: { label: t('jjprop.unknown', 'Unknown'), color: 'default' },
});

const getCallStatus = (t) => ({
  logged: { label: t('jjprop.callLogged', 'Logged'), color: 'default' },
  queued: { label: t('jjprop.callQueued', 'Queued'), color: 'processing' },
  answered: { label: t('jjprop.callAnswered', 'Answered'), color: 'green' },
  completed: { label: t('jjprop.callCompleted', 'Completed'), color: 'green' },
  no_answer: { label: t('jjprop.callNoAnswer', 'No Answer'), color: 'orange' },
  busy: { label: t('jjprop.callBusy', 'Busy'), color: 'orange' },
  failed: { label: t('jjprop.callFailed', 'Failed'), color: 'red' },
});

const getAppointmentStatus = (t) => ({
  proposed: { label: t('jjprop.apptProposed', 'Pending Confirmation'), color: 'orange' },
  confirmed: { label: t('jjprop.apptConfirmed', 'Confirmed'), color: 'green' },
  completed: { label: t('jjprop.apptCompleted', 'Viewed'), color: 'blue' },
  cancelled: { label: t('jjprop.apptCancelled', 'Cancelled'), color: 'red' },
  rescheduled: { label: t('jjprop.apptRescheduled', 'Rescheduled'), color: 'purple' },
});

function tag(config, value) {
  const item = config[value] || { label: value || '-', color: 'default' };
  return <Tag color={item.color}>{item.label}</Tag>;
}

function money(value, currency = 'THB') {
  if (value === null || value === undefined || value === '') return '-';
  return `${new Intl.NumberFormat('zh-TW').format(Number(value || 0))} ${currency}`;
}

function secondsText(value) {
  const seconds = Number(value || 0);
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return min ? `${min}m ${sec}s` : `${sec}s`;
}

function cleanPayload(values) {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
}

export default function JJPropertyManagementPage() {
  const { t } = useTranslation();
  const PROPERTY_STATUS = getPropertyStatus(t);
  const LISTING_TYPES = getListingTypes(t);
  const CALL_RESULT = getCallResult(t);
  const CALL_STATUS = getCallStatus(t);
  const APPOINTMENT_STATUS = getAppointmentStatus(t);
  const [properties, setProperties] = useState([]);
  const [calls, setCalls] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [listingFilter, setListingFilter] = useState('all');
  const [billingMonth, setBillingMonth] = useState(dayjs().format('YYYY-MM'));

  const [propertyModal, setPropertyModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [callModal, setCallModal] = useState(false);
  const [appointmentModal, setAppointmentModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialingId, setDialingId] = useState(null);
  const [aiCallModal, setAiCallModal] = useState(false);

  // Call Management tabs
  const [callerNumbers, setCallerNumbers] = useState([]);
  const [callScripts, setCallScripts] = useState([]);
  const [callSchedules, setCallSchedules] = useState([]);
  const [callerModal, setCallerModal] = useState(false);
  const [editingCaller, setEditingCaller] = useState(null);
  const [scriptModal, setScriptModal] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleFilter, setScheduleFilter] = useState('');

  const [propertyForm] = Form.useForm();
  const [callForm] = Form.useForm();
  const [appointmentForm] = Form.useForm();
  const [callerForm] = Form.useForm();
  const [scriptForm] = Form.useForm();
  const [scheduleForm] = Form.useForm();
  const [aiCallForm] = Form.useForm();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const propertyParams = new URLSearchParams({ limit: '300' });
      if (search) propertyParams.set('search', search);
      if (statusFilter !== 'all') propertyParams.set('status', statusFilter);
      if (listingFilter !== 'all') propertyParams.set('listing_type', listingFilter);

      const [propertyRes, callRes, appointmentRes, billingRes] = await Promise.all([
        fetch(`${API}/properties/manage?${propertyParams}`, { headers: h() }),
        fetch(`${API}/owner-calls?month=${billingMonth}&limit=300`, { headers: h() }),
        fetch(`${API}/viewing-appointments?limit=300`, { headers: h() }),
        fetch(`${API}/owner-calls/billing-summary?month=${billingMonth}`, { headers: h() }),
      ]);

      if (!propertyRes.ok || !callRes.ok || !appointmentRes.ok || !billingRes.ok) {
        throw new Error('load failed');
      }

      const [propertyData, callData, appointmentData, billingData] = await Promise.all([
        propertyRes.json(),
        callRes.json(),
        appointmentRes.json(),
        billingRes.json(),
      ]);
      setProperties(propertyData.properties || []);
      setCalls(callData.call_logs || []);
      setAppointments(appointmentData.appointments || []);
      setBilling(billingData);
    } catch (error) {
      message.error(t('jjprop.loadFailed', 'Failed to load property data'));
    } finally {
      setLoading(false);
    }
  }, [billingMonth, listingFilter, search, statusFilter, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadCallManagement = useCallback(async () => {
    try {
      const [numRes, scrRes] = await Promise.all([
        fetch(`${API}/caller-numbers`, { headers: h() }),
        fetch(`${API}/call-scripts`, { headers: h() }),
      ]);
      if (numRes.ok) setCallerNumbers(await numRes.json());
      if (scrRes.ok) setCallScripts(await scrRes.json());
      let url = `${API}/call-schedules`;
      if (scheduleFilter) url += `?status=${scheduleFilter}`;
      const schRes = await fetch(url, { headers: h() });
      if (schRes.ok) setCallSchedules(await schRes.json());
    } catch { /* ignore */ }
  }, [scheduleFilter]);

  useEffect(() => { loadCallManagement(); }, [loadCallManagement]);

  const stats = useMemo(() => {
    const available = properties.filter(p => p.status === 'available').length;
    const needsFollowup = properties.filter(p => p.status === 'needs_followup').length;
    const scheduled = properties.filter(p => p.status === 'viewing_scheduled').length;
    const rentable = properties.filter(p => ['rent', 'rent_sale'].includes(p.listing_type)).length;
    return { available, needsFollowup, scheduled, rentable };
  }, [properties]);

  const openCreateProperty = () => {
    setEditingProperty(null);
    propertyForm.resetFields();
    propertyForm.setFieldsValue({
      listing_type: 'rent',
      status: 'available',
      priority: 'normal',
      city: 'Bangkok',
      currency: 'THB',
      language: 'thai',
    });
    setPropertyModal(true);
  };

  const openEditProperty = (record) => {
    setEditingProperty(record);
    propertyForm.resetFields();
    propertyForm.setFieldsValue({
      ...record,
      owner_name: record.owner?.name,
      owner_phone: record.owner?.phone || record.owner_phone_snapshot,
      available_from: record.available_from ? dayjs(record.available_from) : null,
    });
    setPropertyModal(true);
  };

  const saveProperty = async () => {
    const values = await propertyForm.validateFields();
    const payload = cleanPayload({
      ...values,
      available_from: values.available_from ? values.available_from.toISOString() : undefined,
    });
    setSaving(true);
    try {
      const url = editingProperty ? `${API}/properties/manage/${editingProperty.id}` : `${API}/properties/manage`;
      const res = await fetch(url, {
        method: editingProperty ? 'PATCH' : 'POST',
        headers: h(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('save failed');
      message.success(editingProperty ? t('jjprop.propertyUpdated', 'Property updated') : t('jjprop.propertyAdded', 'Property added'));
      setPropertyModal(false);
      await loadData();
    } catch (error) {
      message.error(t('jjprop.saveFailed', 'Failed to save property'));
    } finally {
      setSaving(false);
    }
  };

  const openCallLog = (record) => {
    setSelectedProperty(record);
    callForm.resetFields();
    callForm.setFieldsValue({
      provider: 'combotel',
      call_status: 'completed',
      ai_result_status: 'available',
      owner_phone: record.owner?.phone || record.owner_phone_snapshot,
      started_at: dayjs(),
      duration_seconds: 60,
      billable_seconds: 60,
      rate_per_minute: 1,
      currency: 'TWD',
    });
    setCallModal(true);
  };

  const startAICall = (record) => {
    const phone = record.owner?.phone || record.owner_phone_snapshot;
    if (!phone) {
      message.warning(t('jjprop.noOwnerPhone', 'Please add owner phone first'));
      return;
    }
    setSelectedProperty(record);
    aiCallForm.resetFields();
    aiCallForm.setFieldsValue({
      phone,
      property_label: record.title,
      rate_per_minute: 1,
      currency: 'TWD',
      caller_number_id: callerNumbers.find(n => n.is_active)?.id,
      language: "thai"
    });
    setAiCallModal(true);
  };

  const handleConfirmAICall = async () => {
    const values = await aiCallForm.validateFields();
    setDialingId(selectedProperty.id);
    setAiCallModal(false);
    try {
      const res = await fetch(`${API}/properties/manage/${selectedProperty.id}/ai-call`, {
        method: 'POST',
        headers: h(),
        body: JSON.stringify({
          ...values,
          confirm: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.detail || data.message || t('jjprop.aiCallSubmitFailed', 'Failed to submit AI call'));
      }
      message.success(t('jjprop.aiCallSubmitted', 'AI call task submitted'));
      await loadData();
    } catch (error) {
      message.error(String(error.message || error));
    } finally {
      setDialingId(null);
    }
  };

  const saveCallLog = async () => {
    const values = await callForm.validateFields();
    const payload = cleanPayload({
      ...values,
      started_at: values.started_at ? values.started_at.toISOString() : undefined,
      ended_at: values.ended_at ? values.ended_at.toISOString() : undefined,
    });
    setSaving(true);
    try {
      const res = await fetch(`${API}/properties/manage/${selectedProperty.id}/call-logs`, {
        method: 'POST',
        headers: h(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('call save failed');
      message.success(t('jjprop.callLogSaved', 'Call log saved'));
      setCallModal(false);
      await loadData();
    } catch (error) {
      message.error(t('jjprop.callLogSaveFailed', 'Failed to save call log'));
    } finally {
      setSaving(false);
    }
  };

  const openAppointment = (record) => {
    setSelectedProperty(record);
    appointmentForm.resetFields();
    appointmentForm.setFieldsValue({
      property_id: record.id,
      owner_id: record.owner_id,
      owner_phone: record.owner?.phone || record.owner_phone_snapshot,
      status: 'proposed',
      owner_confirmed: false,
      customer_confirmed: false,
      agent_name: t('jjprop.defaultAgent', 'JJ Agent'),
      meeting_place: record.address || record.district,
    });
    setAppointmentModal(true);
  };

  const saveAppointment = async () => {
    const values = await appointmentForm.validateFields();
    const payload = cleanPayload({
      ...values,
      property_id: selectedProperty.id,
      owner_id: selectedProperty.owner_id,
      scheduled_at: values.scheduled_at ? values.scheduled_at.toISOString() : undefined,
    });
    setSaving(true);
    try {
      const res = await fetch(`${API}/viewing-appointments`, {
        method: 'POST',
        headers: h(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('appointment save failed');
      message.success(t('jjprop.appointmentCreated', 'Viewing scheduled'));
      setAppointmentModal(false);
      await loadData();
    } catch (error) {
      message.error(t('jjprop.appointmentCreateFailed', 'Failed to schedule viewing'));
    } finally {
      setSaving(false);
    }
  };

  // Caller Number CRUD
  const saveCallerNumber = async (values) => {
    try {
      const url = editingCaller ? `${API}/caller-numbers/${editingCaller.id}` : `${API}/caller-numbers`;
      const method = editingCaller ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers: h(), body: JSON.stringify(values) });
      if (!res.ok) throw new Error('save failed');
      message.success(editingCaller ? 'Updated' : 'Added');
      setCallerModal(false); callerForm.resetFields(); loadCallManagement();
    } catch (e) { message.error(e.message); }
  };
  const deleteCallerNumber = async (id) => {
    await fetch(`${API}/caller-numbers/${id}`, { method: 'DELETE', headers: h() });
    message.success('Deleted'); loadCallManagement();
  };

  // Call Script CRUD
  const saveScript = async (values) => {
    try {
      const url = editingScript ? `${API}/call-scripts/${editingScript.id}` : `${API}/call-scripts`;
      const method = editingScript ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers: h(), body: JSON.stringify(values) });
      if (!res.ok) throw new Error('save failed');
      message.success(editingScript ? 'Updated' : 'Added');
      setScriptModal(false); scriptForm.resetFields(); loadCallManagement();
    } catch (e) { message.error(e.message); }
  };
  const deleteScript = async (id) => {
    await fetch(`${API}/call-scripts/${id}`, { method: 'DELETE', headers: h() });
    message.success('Deleted'); loadCallManagement();
  };

  // Call Schedule CRUD
  const saveSchedule = async (values) => {
    try {
      const payload = { ...values };
      if (dayjs.isDayjs(values.scheduled_at)) payload.scheduled_at = values.scheduled_at.toISOString();
      const url = editingSchedule ? `${API}/call-schedules/${editingSchedule.id}` : `${API}/call-schedules`;
      const method = editingSchedule ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers: h(), body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('save failed');
      message.success(editingSchedule ? 'Updated' : 'Created');
      setScheduleModal(false); scheduleForm.resetFields(); loadCallManagement();
    } catch (e) { message.error(e.message); }
  };
  const deleteSchedule = async (id) => {
    await fetch(`${API}/call-schedules/${id}`, { method: 'DELETE', headers: h() });
    message.success('Deleted'); loadCallManagement();
  };
  const dialNow = async (record) => {
    try {
      await fetch(`${API}/call-schedules/${record.id}/dial`, { method: 'POST', headers: h() });
      message.info(`Dialing ${record.owner_phone}...`); loadCallManagement();
    } catch (e) { message.error(e.message); }
  };

  const propertyColumns = [
    {
      title: t('jjprop.propertyLabel', 'Property'),
      dataIndex: 'title',
      width: 260,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Space size={4}>
            <Text strong>{record.title}</Text>
            {record.priority === 'high' && <Badge color="red" />}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.property_code || `JJ-${record.id}`} · {record.district || record.city || 'Bangkok'}
          </Text>
        </Space>
      ),
    },
    {
      title: t('jjprop.owner', 'Owner'),
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text><UserOutlined /> {record.owner?.name || t('jjprop.unnamedOwner', 'Unnamed Owner')}</Text>
          <Text type="secondary">{record.owner?.phone || record.owner_phone_snapshot || '-'}</Text>
        </Space>
      ),
    },
    {
      title: t('jjprop.typeStatus', 'Type / Status'),
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {tag(LISTING_TYPES, record.listing_type)}
          {tag(PROPERTY_STATUS, record.status)}
        </Space>
      ),
    },
    {
      title: t('jjprop.price', 'Price'),
      width: 170,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.rent_price ? <Text>{t('jjprop.rent', 'Rent')} {money(record.rent_price, record.currency)}</Text> : null}
          {record.sale_price ? <Text>{t('jjprop.sale', 'Sale')} {money(record.sale_price, record.currency)}</Text> : null}
          {!record.rent_price && !record.sale_price ? <Text type="secondary">{t('jjprop.notSet', 'Not set')}</Text> : null}
        </Space>
      ),
    },
    {
      title: t('jjprop.layout', 'Layout'),
      width: 120,
      render: (_, record) => (
        <Text>{record.bedrooms || '-'} {t('jjprop.bedrooms', 'BR')} / {record.bathrooms || '-'} {t('jjprop.bathrooms', 'BA')}</Text>
      ),
    },
    {
      title: t('jjprop.lastCall', 'Last Call'),
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.last_called_at ? dayjs(record.last_called_at).format('MM/DD HH:mm') : '-'}</Text>
          <Text type="secondary">{CALL_RESULT[record.last_call_status]?.label || record.last_call_status || ''}</Text>
        </Space>
      ),
    },
    {
      title: t('jjprop.aiSummary', 'AI Summary'),
      dataIndex: 'ai_summary',
      ellipsis: true,
      render: text => text || <Text type="secondary">{t('jjprop.noSummary', 'No summary yet')}</Text>,
    },
    {
      title: t('jjprop.actions', 'Actions'),
      width: 170,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title={t('jjprop.editProperty', 'Edit Property')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditProperty(record)} />
          </Tooltip>
          <Tooltip title={t('jjprop.aiCallOwner', 'AI Call Owner')}>
            <Button
              size="small"
              type="primary"
              icon={<RobotOutlined />}
              loading={dialingId === record.id}
              onClick={() => startAICall(record)}
            />
          </Tooltip>
          <Tooltip title={t('jjprop.manualCallLog', 'Manual Call Log')}>
            <Button size="small" icon={<PhoneOutlined />} onClick={() => openCallLog(record)} />
          </Tooltip>
          <Tooltip title={t('jjprop.scheduleViewing', 'Schedule Viewing')}>
            <Button size="small" type="primary" icon={<CalendarOutlined />} onClick={() => openAppointment(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const callColumns = [
    {
      title: t('jjprop.time', 'Time'),
      width: 150,
      render: (_, record) => dayjs(record.started_at || record.created_at).format('YYYY/MM/DD HH:mm'),
    },
    {
      title: t('jjprop.propertyLabel', 'Property'),
      width: 220,
      render: (_, record) => record.property?.title || '-',
    },
    {
      title: t('jjprop.ownerPhone', 'Owner Phone'),
      dataIndex: 'owner_phone',
      width: 150,
      render: text => text || '-',
    },
    {
      title: t('jjprop.statusLabel', 'Status'),
      width: 170,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {tag(CALL_STATUS, record.call_status)}
          {tag(CALL_RESULT, record.ai_result_status)}
        </Space>
      ),
    },
    {
      title: t('jjprop.rent', 'Rent'),
      dataIndex: 'rent',
      width: 100,
      render: text => text || '-',
    },
    {
      title: t('jjprop.area', 'Area'),
      dataIndex: 'area',
      width: 120,
      render: text => text || '-',
    },
    {
      title: t('jjprop.viewingTime', 'Viewing Time'),
      dataIndex: 'viewing_time',
      width: 140,
      render: text => text || '-',
    },
    {
      title: t('jjprop.secondsCost', 'Seconds / Cost'),
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{secondsText(record.billable_seconds || record.duration_seconds)}</Text>
          <Text type="secondary">{money(record.cost_amount, record.currency || 'TWD')}</Text>
        </Space>
      ),
    },
    {
      title: t('jjprop.summary', 'Summary'),
      dataIndex: 'summary',
      ellipsis: true,
      render: text => text || <Text type="secondary">{t('jjprop.noSummary', 'No summary')}</Text>,
    },
  ];

  const appointmentColumns = [
    {
      title: t('jjprop.viewingTime', 'Viewing Time'),
      width: 170,
      render: (_, record) => record.scheduled_at ? dayjs(record.scheduled_at).format('YYYY/MM/DD HH:mm') : '-',
    },
    {
      title: t('jjprop.propertyLabel', 'Property'),
      width: 240,
      render: (_, record) => record.property?.title || '-',
    },
    {
      title: t('jjprop.contactInfo', 'Contact Info'),
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{t('jjprop.customer', 'Customer')} {record.customer_phone || record.customer?.phone || '-'}</Text>
          <Text type="secondary">{t('jjprop.owner', 'Owner')} {record.owner_phone || record.owner?.phone || '-'}</Text>
        </Space>
      ),
    },
    {
      title: t('jjprop.confirmation', 'Confirmation'),
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {tag(APPOINTMENT_STATUS, record.status)}
          <Space size={4}>
            <Tag color={record.owner_confirmed ? 'green' : 'default'}>{t('jjprop.owner', 'Owner')}</Tag>
            <Tag color={record.customer_confirmed ? 'green' : 'default'}>{t('jjprop.customer', 'Customer')}</Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: t('jjprop.agentPlace', 'Agent / Place'),
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.agent_name || '-'}</Text>
          <Text type="secondary">{record.meeting_place || '-'}</Text>
        </Space>
      ),
    },
    {
      title: t('common.notes', 'Notes'),
      dataIndex: 'notes',
      ellipsis: true,
      render: text => text || '-',
    },
  ];

  const billingOwnerColumns = [
    { title: t('jjprop.owner', 'Owner'), dataIndex: 'owner_name' },
    { title: t('jjprop.phoneLabel', 'Phone'), dataIndex: 'owner_phone' },
    { title: t('jjprop.callCount', 'Calls'), dataIndex: 'calls', width: 100 },
    { title: t('jjprop.billableSeconds', 'Billable Seconds'), dataIndex: 'billable_seconds', width: 120, render: secondsText },
    { title: t('jjprop.cost', 'Cost'), width: 140, render: (_, record) => money(record.cost_amount, record.currency || billing?.currency || 'TWD') },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space align="center" style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <HomeOutlined style={{ fontSize: 24, color: '#1677ff' }} />
          <div>
            <Title level={3} style={{ margin: 0 }}>{t('jjprop.pageTitle', 'Property Management')}</Title>
            <Text type="secondary">{t('jjprop.pageSubtitle', 'Owner properties, AI calls, viewing schedules & monthly billing')}</Text>
          </div>
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>{t('common.refresh', 'Refresh')}</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateProperty}>{t('jjprop.addProperty', 'Add Property')}</Button>
        </Space>
      </Space>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} lg={5}>
          <Card size="small"><Statistic title={t('jjprop.statAvailable', 'Available')} value={stats.available} prefix={<CheckCircleOutlined />} /></Card>
        </Col>
        <Col xs={12} lg={5}>
          <Card size="small"><Statistic title={t('jjprop.statRentable', 'Rentable')} value={stats.rentable} prefix={<HomeOutlined />} /></Card>
        </Col>
        <Col xs={12} lg={5}>
          <Card size="small"><Statistic title={t('jjprop.statViewing', 'Viewing Scheduled')} value={stats.scheduled} prefix={<CalendarOutlined />} /></Card>
        </Col>
        <Col xs={12} lg={5}>
          <Card size="small"><Statistic title={t('jjprop.statFollowup', 'Needs Follow-up')} value={stats.needsFollowup} prefix={<ClockCircleOutlined />} /></Card>
        </Col>
        <Col xs={24} lg={4}>
          <Card size="small"><Statistic title={`${billingMonth} ${t('jjprop.callFee', 'Call Fees')}`} value={billing?.cost_amount || 0} precision={2} suffix={billing?.currency || 'TWD'} prefix={<DollarOutlined />} /></Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            prefix={<SearchOutlined />}
            placeholder={t('jjprop.searchPlaceholder', 'Search property, owner, phone, area')}
            value={search}
            onChange={event => setSearch(event.target.value)}
            allowClear
            style={{ width: 280 }}
          />
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 150 }}>
            <Option value="all">{t('jjprop.allStatuses', 'All Statuses')}</Option>
            {Object.entries(PROPERTY_STATUS).map(([key, value]) => (
              <Option key={key} value={key}>{value.label}</Option>
            ))}
          </Select>
          <Select value={listingFilter} onChange={setListingFilter} style={{ width: 140 }}>
            <Option value="all">{t('jjprop.allTypes', 'All Types')}</Option>
            {Object.entries(LISTING_TYPES).map(([key, value]) => (
              <Option key={key} value={key}>{value.label}</Option>
            ))}
          </Select>
          <DatePicker
            picker="month"
            value={dayjs(`${billingMonth}-01`)}
            onChange={value => setBillingMonth(value ? value.format('YYYY-MM') : dayjs().format('YYYY-MM'))}
            allowClear={false}
          />
          <Text type="secondary">{t('jjprop.totalProps', 'Total')} {properties.length} {t('jjprop.propertiesWord', 'properties')}</Text>
        </Space>
      </Card>

      <Tabs
        defaultActiveKey="properties"
        items={[
          {
            key: 'properties',
            label: t('jjprop.tabProperties', 'Properties'),
            children: (
              <Table
                columns={propertyColumns}
                dataSource={properties}
                rowKey="id"
                loading={loading}
                size="small"
                scroll={{ x: 1320 }}
                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: total => `${t('jjprop.totalRecords', 'Total')} ${total}` }}
              />
            ),
          },
          {
            key: 'calls',
            label: t('jjprop.tabCalls', 'AI Call Logs'),
            children: (
              <Table
                columns={callColumns}
                dataSource={calls}
                rowKey="id"
                loading={loading}
                size="small"
                scroll={{ x: 1050 }}
                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: total => `${t('jjprop.totalRecords', 'Total')} ${total}` }}
              />
            ),
          },
          {
            key: 'appointments',
            label: t('jjprop.tabAppointments', 'Viewing Schedules'),
            children: (
              <Table
                columns={appointmentColumns}
                dataSource={appointments}
                rowKey="id"
                loading={loading}
                size="small"
                scroll={{ x: 1150 }}
                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: total => `${t('jjprop.totalRecords', 'Total')} ${total}` }}
              />
            ),
          },
          {
            key: 'billing',
            label: t('jjprop.tabBilling', 'Monthly Billing'),
            children: (
              <>
                <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                  <Col xs={12} md={6}><Card size="small"><Statistic title={t('jjprop.callCount', 'Calls')} value={billing?.total_calls || 0} prefix={<PhoneOutlined />} /></Card></Col>
                  <Col xs={12} md={6}><Card size="small"><Statistic title={t('jjprop.answeredCalls', 'Answered')} value={billing?.answered_calls || 0} prefix={<CheckCircleOutlined />} /></Card></Col>
                  <Col xs={12} md={6}><Card size="small"><Statistic title={t('jjprop.billableTime', 'Billable Time')} value={secondsText(billing?.billable_seconds || 0)} prefix={<ClockCircleOutlined />} /></Card></Col>
                  <Col xs={12} md={6}><Card size="small"><Statistic title={t('jjprop.monthlyCost', 'Monthly Cost')} value={billing?.cost_amount || 0} precision={2} suffix={billing?.currency || 'TWD'} prefix={<DollarOutlined />} /></Card></Col>
                </Row>
                <Table
                  columns={billingOwnerColumns}
                  dataSource={billing?.by_owner || []}
                  rowKey={record => record.owner_id || record.owner_phone || 'unassigned'}
                  size="small"
                  pagination={false}
                />
              </>
            ),
          },
          {
            key: 'caller-numbers',
            label: <span><NumberOutlined /> Caller Numbers</span>,
            children: (
              <Card size="small" title="Caller Phone Numbers"
                extra={<Button size="small" icon={<PlusOutlined />} type="primary"
                  onClick={() => { setEditingCaller(null); callerForm.resetFields(); setCallerModal(true); }}>
                  Add Number
                </Button>}>
                <Table dataSource={callerNumbers} rowKey="id" size="small" pagination={false}
                  columns={[
                    { title: 'Phone', dataIndex: 'phone_number', render: v => <Text code>{v}</Text> },
                    { title: 'Label', dataIndex: 'label' },
                    { title: 'Provider', dataIndex: 'provider', width: 100, render: v => v && String(v).toLowerCase().includes('telnyx') ? 'combotel' : (v || '-') },
                    { title: 'Active', dataIndex: 'is_active', width: 80, render: v => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag> },
                    { title: 'Notes', dataIndex: 'notes', ellipsis: true },
                    { title: 'Actions', width: 120, render: (_, r) => (
                      <Space size="small">
                        <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingCaller(r); callerForm.setFieldsValue(r); setCallerModal(true); }} />
                        <Popconfirm title="Delete?" onConfirm={() => deleteCallerNumber(r.id)}>
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    )},
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'call-scripts',
            label: <span><FileTextOutlined /> Call Scripts</span>,
            children: (
              <Card size="small" title="Call Script Templates"
                extra={<Button size="small" icon={<PlusOutlined />} type="primary"
                  onClick={() => { setEditingScript(null); scriptForm.resetFields(); setScriptModal(true); }}>
                  Add Script
                </Button>}>
                <Table dataSource={callScripts} rowKey="id" size="small" pagination={false}
                  columns={[
                    { title: 'Name', dataIndex: 'name', width: 200 },
                    { title: 'Category', dataIndex: 'category', width: 120, render: v => <Tag>{v}</Tag> },
                    { title: 'Content', dataIndex: 'script_content', render: v => <div style={{ maxWidth: 500, whiteSpace: 'pre-wrap', fontSize: 12 }}>{v}</div> },
                    { title: 'Active', dataIndex: 'is_active', width: 80, render: v => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag> },
                    { title: 'Actions', width: 120, render: (_, r) => (
                      <Space size="small">
                        <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingScript(r); scriptForm.setFieldsValue(r); setScriptModal(true); }} />
                        <Popconfirm title="Delete?" onConfirm={() => deleteScript(r.id)}>
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    )},
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'call-schedules',
            label: <span><ScheduleOutlined /> Call Schedules</span>,
            children: (
              <Card size="small" title="Scheduled Owner Calls"
                extra={
                  <Space size="small">
                    <Select allowClear placeholder="Filter" style={{ width: 130 }} value={scheduleFilter || undefined}
                      onChange={v => setScheduleFilter(v || '')}
                      options={['pending', 'dialing', 'completed', 'failed', 'cancelled'].map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
                    />
                    <Button size="small" icon={<PlusOutlined />} type="primary"
                      onClick={() => { setEditingSchedule(null); scheduleForm.resetFields(); setScheduleModal(true); }}>
                      Schedule Call
                    </Button>
                  </Space>
                }>
                <Table dataSource={callSchedules} rowKey="id" size="small"
                  columns={[
                    { title: 'Owner', dataIndex: 'owner_name', width: 110 },
                    { title: 'Phone', dataIndex: 'owner_phone', width: 130, render: v => <Text code>{v}</Text> },
                    { title: 'Lang', dataIndex: 'language', width: 80, render: v => <Tag>{v}</Tag> },
                    { title: 'Scheduled', dataIndex: 'scheduled_at', width: 160, render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-', sorter: (a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at), defaultSortOrder: 'ascend' },
                    { title: 'Status', dataIndex: 'call_status', width: 100, render: v => <Tag color={v === 'completed' ? 'green' : v === 'dialing' ? 'processing' : v === 'failed' ? 'red' : v === 'cancelled' ? 'warning' : 'default'}>{v}</Tag> },
                    { title: 'Caller', dataIndex: 'caller_number_id', width: 130, render: v => { const cn = callerNumbers.find(n => n.id === v); return cn ? <Text style={{ fontSize: 12 }}>{cn.phone_number}</Text> : '-'; } },
                    { title: 'Script', dataIndex: 'script_id', width: 120, render: v => { const sc = callScripts.find(s => s.id === v); return sc ? <Tag color="blue">{sc.name}</Tag> : '-'; } },
                    { title: 'Notes', dataIndex: 'notes', ellipsis: true, width: 100 },
                    { title: 'Actions', width: 180, render: (_, r) => (
                      <Space size="small">
                        <Button size="small" icon={<PlayCircleOutlined />} type="primary" onClick={() => dialNow(r)} disabled={r.call_status === 'completed' || r.call_status === 'dialing'}>Dial</Button>
                        <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingSchedule(r); scheduleForm.setFieldsValue({ ...r, scheduled_at: r.scheduled_at ? dayjs(r.scheduled_at) : undefined }); setScheduleModal(true); }} />
                        <Popconfirm title="Delete?" onConfirm={() => deleteSchedule(r.id)}>
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    )},
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title={editingProperty ? t('jjprop.editProperty', 'Edit Property') : t('jjprop.addOwnerProperty', 'Add Owner Property')}
        open={propertyModal}
        onCancel={() => setPropertyModal(false)}
        onOk={saveProperty}
        confirmLoading={saving}
        width={760}
        okText={t('common.save', 'Save')}
        cancelText={t('common.cancel', 'Cancel')}
      >
        <Form form={propertyForm} layout="vertical">
          <Divider orientation="left">{t('jjprop.owner', 'Owner')}</Divider>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="owner_name" label={t('jjprop.ownerName', 'Owner Name')}><Input prefix={<UserOutlined />} /></Form.Item></Col>
            <Col span={12}><Form.Item name="owner_phone" label={t('jjprop.ownerPhone', 'Owner Phone')} rules={[{ required: true, message: t('jjprop.requireOwnerPhone', 'Please enter owner phone') }]}><Input prefix={<PhoneOutlined />} /></Form.Item></Col>
          </Row>
          <Divider orientation="left">{t('jjprop.propertyLabel', 'Property')}</Divider>
          <Row gutter={12}>
            <Col span={16}><Form.Item name="title" label={t('jjprop.propertyName', 'Property Name')} rules={[{ required: true, message: t('jjprop.requirePropertyName', 'Please enter property name') }]}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="property_code" label={t('jjprop.propertyCode', 'Property Code')}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="listing_type" label={t('jjprop.typeLabel', 'Type')} rules={[{ required: true }]}>
                <Select>{Object.entries(LISTING_TYPES).map(([key, value]) => <Option key={key} value={key}>{value.label}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label={t('jjprop.statusLabel', 'Status')} rules={[{ required: true }]}>
                <Select>{Object.entries(PROPERTY_STATUS).map(([key, value]) => <Option key={key} value={key}>{value.label}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label={t('jjprop.priority', 'Priority')}>
                <Select>
                  <Option value="high">{t('jjprop.priorityHigh', 'High')}</Option>
                  <Option value="normal">{t('jjprop.priorityNormal', 'Normal')}</Option>
                  <Option value="low">{t('jjprop.priorityLow', 'Low')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="property_type" label={t('jjprop.propertyType', 'Property Type')}><Input placeholder="Condo / House" /></Form.Item></Col>
            <Col span={6}><Form.Item name="bedrooms" label={t('jjprop.bedrooms', 'Bedrooms')}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="bathrooms" label={t('jjprop.bathrooms', 'Bathrooms')}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}>
              <Form.Item name="language" label={t('jjprop.prefLanguage', 'Owner Language')}>
                <Select options={[
                  { value: 'thai', label: 'Thai' },
                  { value: 'english', label: 'English' },
                  { value: 'chinese', label: 'Chinese' },
                ]} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="rent_price" label={t('jjprop.rent', 'Rent')}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="sale_price" label={t('jjprop.salePrice', 'Sale Price')}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="currency" label={t('jjprop.currency', 'Currency')}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="city" label={t('jjprop.city', 'City')}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="district" label={t('jjprop.district', 'District')}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="available_from" label={t('jjprop.availableFrom', 'Available From')}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="address" label={t('jjprop.address', 'Address')}><Input /></Form.Item>
          <Form.Item name="notes" label={t('jjprop.internalNotes', 'Internal Notes')}><TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`${t('jjprop.newCallLog', 'New AI Call Log')}: ${selectedProperty?.title || ''}`}
        open={callModal}
        onCancel={() => setCallModal(false)}
        onOk={saveCallLog}
        confirmLoading={saving}
        width={720}
        okText={t('common.save', 'Save')}
        cancelText={t('common.cancel', 'Cancel')}
      >
        <Form form={callForm} layout="vertical">
          <Row gutter={12}>
            <Col span={8}><Form.Item name="provider" label={t('jjprop.provider', 'Provider')}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="owner_phone" label={t('jjprop.ownerPhone', 'Owner Phone')}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="caller_id" label={t('jjprop.callerId', 'Caller ID')}><Input placeholder="+13463760047" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="call_status" label={t('jjprop.callStatusLabel', 'Call Status')}>
                <Select>{Object.entries(CALL_STATUS).map(([key, value]) => <Option key={key} value={key}>{value.label}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="ai_result_status" label={t('jjprop.aiResult', 'AI Result')}>
                <Select>{Object.entries(CALL_RESULT).map(([key, value]) => <Option key={key} value={key}>{value.label}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={8}><Form.Item name="started_at" label={t('jjprop.callTime', 'Call Time')}><DatePicker showTime style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="duration_seconds" label={t('jjprop.durationSeconds', 'Duration (sec)')}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="billable_seconds" label={t('jjprop.billableSeconds', 'Billable Seconds')}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="rate_per_minute" label={t('jjprop.ratePerMinute', 'Rate / Minute')}><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="currency" label={t('jjprop.currency', 'Currency')}><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="summary" label={t('jjprop.aiSummary', 'AI Summary')} rules={[{ required: true, message: t('jjprop.requireAiResult', 'Please enter AI call result') }]}>
            <TextArea rows={3} placeholder={t('jjprop.summaryPlaceholder', 'e.g. Owner said the property is still available, can view Saturday afternoon.')} />
          </Form.Item>
          <Form.Item name="transcript" label={t('jjprop.transcript', 'Transcript')}><TextArea rows={4} /></Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`${t('jjprop.scheduleViewingTitle', 'Schedule Viewing')}: ${selectedProperty?.title || ''}`}
        open={appointmentModal}
        onCancel={() => setAppointmentModal(false)}
        onOk={saveAppointment}
        confirmLoading={saving}
        width={700}
        okText={t('common.create', 'Create')}
        cancelText={t('common.cancel', 'Cancel')}
      >
        <Form form={appointmentForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}><Form.Item name="scheduled_at" label={t('jjprop.viewingTime', 'Viewing Time')} rules={[{ required: true, message: t('jjprop.requireViewingTime', 'Please select viewing time') }]}><DatePicker showTime style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}>
              <Form.Item name="status" label={t('jjprop.statusLabel', 'Status')}>
                <Select>{Object.entries(APPOINTMENT_STATUS).map(([key, value]) => <Option key={key} value={key}>{value.label}</Option>)}</Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="agent_name" label={t('jjprop.agent', 'Agent')}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="customer_phone" label={t('jjprop.customerPhone', 'Customer Phone')}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="owner_phone" label={t('jjprop.ownerPhone', 'Owner Phone')}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="owner_confirmed" label={t('jjprop.ownerConfirmed', 'Owner Confirmed')} valuePropName="checked"><Switch /></Form.Item></Col>
            <Col span={12}><Form.Item name="customer_confirmed" label={t('jjprop.customerConfirmed', 'Customer Confirmed')} valuePropName="checked"><Switch /></Form.Item></Col>
          </Row>
          <Form.Item name="meeting_place" label={t('jjprop.meetingPlace', 'Meeting Place')}><Input /></Form.Item>
          <Form.Item name="notes" label={t('common.notes', 'Notes')}><TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>
      <Modal title={editingCaller ? 'Edit Caller Number' : 'Add Caller Number'}
        open={callerModal} onCancel={() => setCallerModal(false)} onOk={() => callerForm.submit()} width={480}>
        <Form form={callerForm} layout="vertical" onFinish={saveCallerNumber}>
          <Form.Item name="phone_number" label="Phone Number" rules={[{ required: true }]}><Input placeholder="+13463760047" /></Form.Item>
          <Form.Item name="label" label="Label" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="provider" label="Provider" initialValue="telnyx"><Select options={[{ value: 'telnyx', label: 'combotel' }, { value: 'twilio', label: 'Twilio' }]} /></Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}><Switch /></Form.Item>
          <Form.Item name="notes" label="Notes"><TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      <Modal title={editingScript ? 'Edit Call Script' : 'Add Call Script'}
        open={scriptModal} onCancel={() => setScriptModal(false)} onOk={() => scriptForm.submit()} width={600}>
        <Form form={scriptForm} layout="vertical" onFinish={saveScript}>
          <Form.Item name="name" label="Script Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="category" label="Category" initialValue="rent_inquiry">
            <Select options={[
              { value: 'rent_inquiry', label: 'Rent Inquiry' }, { value: 'sale_inquiry', label: 'Sale Inquiry' },
              { value: 'follow_up', label: 'Follow Up' }, { value: 'viewing_confirm', label: 'Viewing Confirm' },
              { value: 'renewal_reminder', label: 'Renewal Reminder' },
              { value: 'availability_check', label: 'Life Property Availability Check' },
            ]} />
          </Form.Item>
          <Form.Item name="script_content" label="Script Content" rules={[{ required: true }]}>
            <TextArea rows={6} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}><Switch /></Form.Item>
        </Form>
      </Modal>

      <Modal title={editingSchedule ? 'Edit Call Schedule' : 'Schedule Call'}
        open={scheduleModal} onCancel={() => setScheduleModal(false)} onOk={() => scheduleForm.submit()} width={560}>
        <Form form={scheduleForm} layout="vertical" onFinish={saveSchedule}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="owner_name" label="Owner Name"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="owner_phone" label="Owner Phone" rules={[{ required: true }]}><Input placeholder="+66912345678" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="scheduled_at" label="Scheduled Time" rules={[{ required: true }]}><DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="call_status" label="Status" initialValue="pending">
              <Select options={[{ value: 'pending', label: 'Pending' }, { value: 'completed', label: 'Completed' }, { value: 'cancelled', label: 'Cancelled' }]} />
            </Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="caller_number_id" label="Caller Number">
              <Select allowClear placeholder="Select..." options={callerNumbers.map(n => ({ value: n.id, label: n.label + ' (' + n.phone_number + ')' }))} />
            </Form.Item></Col>
            <Col span={12}><Form.Item name="script_id" label="Call Script">
              <Select allowClear placeholder="Select..." options={callScripts.map(s => ({ value: s.id, label: s.name }))} />
            </Form.Item></Col>
          </Row>
          <Form.Item name="notes" label="Notes"><TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>


      {/* ── AI Call Confirmation Modal ── */}
      <Modal title={t('jjprop.confirmAICall', 'Confirm AI call to owner')}
        open={aiCallModal} onCancel={() => setAiCallModal(false)} onOk={handleConfirmAICall} width={480}
        okText={t('jjprop.confirmDial', 'Confirm Call')} okButtonProps={{ danger: true }}>
        <Form form={aiCallForm} layout="vertical">
          <Text strong>{t('jjprop.propertyLabel', 'Property')}: {selectedProperty?.title}</Text>
          <div style={{ marginBottom: 16 }} />
          <Form.Item name="phone" label={t('jjprop.phoneLabel', 'Phone')} rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="language" label={t('jjprop.language', 'Language')}>
            <Select options={[
              { value: 'th', label: 'Thai (泰文)' },
              { value: 'en', label: 'English (英文)' },
              { value: 'zh-TW', label: 'Chinese (中文)' },
            ]} />
          </Form.Item>
          <Form.Item name="caller_number_id" label={t('jjprop.callerId', 'Caller Number')}>
            <Select placeholder="Select..." options={callerNumbers.map(n => ({ value: n.id, label: n.label + ' (' + n.phone_number + ')' }))} />
          </Form.Item>
          <Form.Item name="script_id" label="Call Script Template">
            <Select allowClear placeholder="Select script template..." options={callScripts.map(s => ({ value: s.id, label: s.name }))} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="rate_per_minute" label={t('jjprop.ratePerMinute', 'Rate / Min')}><InputNumber min={0} step={0.1} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="currency" label={t('jjprop.currency', 'Currency')}><Input /></Form.Item></Col>
          </Row>
          <Text type="secondary">{t('jjprop.aiCallHint', 'This will make a real call and the result will be written back to this platform.')}</Text>
        </Form>
      </Modal>
    </div>
  );
}
